export const defaultSyntheticKlvTemplateJson = JSON.stringify({
  11: "NO SIGNAL",
  47: 32,
  48: {
    1: "UNCLASSIFIED",
    2: "GENC_TWO_LETTER",
    3: "//US",
    12: "GENC_TWO_LETTER",
    13: "US",
  },
  65: 16,
}, null, 2);

function assertNoDuplicateObjectKeys(text) {
  let offset = 0;

  const skipWhitespace = () => {
    while (/\s/.test(text[offset] ?? "")) offset += 1;
  };

  const readString = () => {
    const start = offset;
    offset += 1;
    while (offset < text.length) {
      if (text[offset] === "\\") {
        offset += 2;
      } else if (text[offset] === '"') {
        offset += 1;
        return JSON.parse(text.slice(start, offset));
      } else {
        offset += 1;
      }
    }
    return "";
  };

  const readValue = (path) => {
    skipWhitespace();
    if (text[offset] === "{") {
      offset += 1;
      skipWhitespace();
      const keys = new Map();
      while (text[offset] !== "}") {
        const key = readString();
        const keyIdentity = /^\d+$/.test(key) ? key.replace(/^0+(?=\d)/, "") : key;
        if (keys.has(keyIdentity)) {
          const location = path.length > 0 ? ` in tag ${path.join(".")}` : "";
          const firstKey = keys.get(keyIdentity);
          const conflict = firstKey === key ? "" : ` conflicts with “${firstKey}”`;
          throw new Error(`Duplicate tag key “${key}”${conflict}${location} is not allowed.`);
        }
        keys.set(keyIdentity, key);
        skipWhitespace();
        offset += 1; // Colon. JSON.parse has already verified the document syntax.
        readValue([...path, key]);
        skipWhitespace();
        if (text[offset] === ",") {
          offset += 1;
          skipWhitespace();
        } else {
          break;
        }
      }
      offset += 1;
      return;
    }
    if (text[offset] === "[") {
      offset += 1;
      skipWhitespace();
      let index = 0;
      while (text[offset] !== "]") {
        readValue([...path, `[${index}]`]);
        index += 1;
        skipWhitespace();
        if (text[offset] === ",") {
          offset += 1;
          skipWhitespace();
        } else {
          break;
        }
      }
      offset += 1;
      return;
    }
    if (text[offset] === '"') {
      readString();
      return;
    }
    while (offset < text.length && !/[\s,}\]]/.test(text[offset])) offset += 1;
  };

  readValue([]);
}

export function parseSyntheticKlvTemplateDocument(text) {
  let parsed;
  try {
    parsed = JSON.parse(text);
  } catch (error) {
    throw new Error(`Invalid JSON: ${error.message}`);
  }
  assertNoDuplicateObjectKeys(text);
  if (!parsed || Array.isArray(parsed) || typeof parsed !== "object") {
    throw new Error("The template must be a JSON object keyed by MISB tag number.");
  }
  for (const key of Object.keys(parsed)) {
    if (!/^\d+$/.test(key) || Number(key) < 1 || Number(key) > 255) {
      throw new Error(`Tag key “${key}” must be an integer from 1 through 255.`);
    }
  }
  if (Object.prototype.hasOwnProperty.call(parsed, "1")) {
    throw new Error("Tag 1 is generated automatically and cannot be configured.");
  }
  if (Object.prototype.hasOwnProperty.call(parsed, "2")) {
    throw new Error("Tag 2 is generated automatically and cannot be configured.");
  }
  const revision = parsed["65"];
  if (!Number.isInteger(revision) || revision < 1 || revision > 19) {
    throw new Error("Tag 65 is required and must be an integer from 1 through 19.");
  }
  const fields = { ...parsed };
  delete fields["65"];
  return {
    canonicalJson: JSON.stringify(parsed, null, 2),
    fieldsJson: JSON.stringify(fields),
    revision,
  };
}

export function syntheticKlvNativeTemplate(templateJson) {
  try {
    const parsed = parseSyntheticKlvTemplateDocument(templateJson);
    return {
      syntheticKlvFieldsJson: parsed.fieldsJson,
      syntheticKlvRevision: parsed.revision,
    };
  } catch {
    const fallback = parseSyntheticKlvTemplateDocument(defaultSyntheticKlvTemplateJson);
    return {
      syntheticKlvFieldsJson: fallback.fieldsJson,
      syntheticKlvRevision: fallback.revision,
    };
  }
}

export function syntheticKlvTemplateDocument(fieldsJson, revision) {
  try {
    const fields = JSON.parse(fieldsJson);
    if (!fields || Array.isArray(fields) || typeof fields !== "object") throw new Error("invalid fields");
    return parseSyntheticKlvTemplateDocument(JSON.stringify({ ...fields, 65: revision })).canonicalJson;
  } catch {
    return defaultSyntheticKlvTemplateJson;
  }
}
